import { useEffect, useState } from 'react';
import { AlertTriangle, Calculator, Download, Lock, RefreshCw } from 'lucide-react';
import { Badge, Button, Drawer, Page, Panel, StatCard, Tabs, Toast } from '../../admin-ui';
import { api } from '../../services/api';
import { formatMoney } from '../../lib/billing';

function today() {
  return new Date().toLocaleDateString('en-CA');
}

function token() {
  return localStorage.getItem('neon_token') || localStorage.getItem('token') || '';
}

export default function AdminCashClose() {
  const [active, setActive] = useState('today');
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingDays, setPendingDays] = useState([]);
  const [pendingReports, setPendingReports] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [x, z, pd] = await Promise.all([
        api.get('/cashclose/today'),
        api.get('/cashclose?type=Z').catch(() => ({ items: [] })),
        api.get('/cashclose/pending-days').catch(() => ({ pendingDays: [] }))
      ]);
      setReport(x);
      setHistory(z.items || []);
      const days = pd.pendingDays || [];
      setPendingDays(days);
      const reports = {};
      await Promise.all(days.map(async (day) => {
        try {
          reports[day] = await api.post('/cashclose/x', { date: day });
        } catch {
          // Pending totals are informative; failing one report must not block the page.
        }
      }));
      setPendingReports(reports);
    } catch (err) {
      setToast({ variant: 'danger', message: err.message || 'No se pudo cargar caja' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closeZForDate = async (date) => {
    const text = window.prompt(`Escribe CERRAR para confirmar el cierre Z del ${date}`);
    if (text !== 'CERRAR') return;
    try {
      const created = await api.post('/cashclose/z', { date });
      setToast({ variant: 'success', message: `Cierre Z de ${date} creado` });
      setSelected(created);
      await load();
    } catch (err) {
      setToast({ variant: 'danger', message: err.message || 'No se pudo cerrar caja' });
    }
  };

  const downloadPdf = async (item) => {
    const res = await fetch(`/api/cashclose/${item._id}/pdf`, { headers: { Authorization: `Bearer ${token()}` } });
    if (!res.ok) throw new Error('No se pudo descargar el PDF Z');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cierre-${item.type}-${item.date}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Page
      title="Cierre de caja"
      description="Consulta X en vivo y cierres Z inmutables"
      actions={<Button variant="secondary" iconLeft={<RefreshCw size={16} />} loading={loading} onClick={load}>Actualizar</Button>}
    >
      <Toast {...toast} onClose={() => setToast(null)} />
      <Tabs tabs={[{ key: 'today', label: 'Hoy (X)' }, { key: 'history', label: 'Histórico Z' }]} active={active} onChange={setActive} />

      {pendingDays.length > 0 && (
        <div className="acc-pending-alert">
          <div className="acc-pending-header">
            <AlertTriangle size={18} />
            <strong>
              {pendingDays.length === 1
                ? '1 día con cobros sin cerrar'
                : `${pendingDays.length} días con cobros sin cerrar`}
            </strong>
            <span className="acc-pending-subtitle">
              Debes cerrarlos en orden antes de poder cerrar el día actual.
            </span>
          </div>
          <div className="acc-pending-list">
            {pendingDays.map((day, index) => {
              const pendingReport = pendingReports[day];
              const isFirst = index === 0;
              return (
                <div key={day} className={`acc-pending-row${isFirst ? ' is-active' : ' is-blocked'}`}>
                  <div className="acc-pending-date">
                    {day}
                    {!isFirst && <Lock size={12} className="acc-pending-lock" />}
                  </div>
                  <div className="acc-pending-total">
                    {pendingReport ? formatMoney(pendingReport.totalGross) : '...'}
                  </div>
                  <Button
                    variant="danger"
                    className="acc-pending-action"
                    disabled={!isFirst}
                    onClick={() => closeZForDate(day)}
                  >
                    Cerrar Z
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {active === 'today' && report && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '.75rem' }}>
            <StatCard label="Bruto" value={formatMoney(report.totalGross)} index={0} />
            <StatCard label="Neto" value={formatMoney(report.totalNet)} index={1} />
            <StatCard label="IVA" value={formatMoney(report.totalTax)} index={2} />
            <StatCard label="Facturas" value={report.invoiceCount || 0} index={3} />
            <StatCard label="Ticket medio" value={formatMoney(report.averageTicket)} index={4} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            <Panel title="Por método de pago">
              {(report.paymentBreakdown || []).map((row) => (
                <div key={row.method} className="billing-row"><span>{row.method}</span><strong>{formatMoney(row.amount)}</strong></div>
              ))}
            </Panel>
            <Panel title="Por categoría">
              {(report.categoryBreakdown || []).map((row) => (
                <div key={row.category} className="billing-row"><span>{row.category} · {row.units} uds</span><strong>{formatMoney(row.amount)}</strong></div>
              ))}
            </Panel>
            <Panel title="Por mesa">
              {(report.tablesServed || []).map((row) => (
                <div key={row.tableId || row.tableNumber} className="billing-row"><span>M{row.tableNumber || '?'} · {row.invoiceCount} fact.</span><strong>{formatMoney(row.amount)}</strong></div>
              ))}
            </Panel>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="danger"
              iconLeft={<Calculator size={16} />}
              disabled={pendingDays.length > 0}
              title={pendingDays.length > 0 ? 'Primero cierra los días pendientes' : ''}
              onClick={() => closeZForDate(today())}
            >
              {pendingDays.length > 0
                ? `Cerrar caja Z (${pendingDays.length} pendiente${pendingDays.length > 1 ? 's' : ''})`
                : 'Cerrar caja Z'}
            </Button>
          </div>
        </div>
      )}

      {active === 'history' && (
        <Panel title="Cierres Z">
          <table className="billing-table">
            <thead><tr><th>Fecha</th><th>Tipo</th><th>Facturas</th><th>Bruto</th><th>Estado</th></tr></thead>
            <tbody>
              {history.map((item) => (
                <tr key={item._id} onClick={() => setSelected(item)}>
                  <td>{item.date}</td>
                  <td>{item.type}</td>
                  <td>{item.invoiceCount}</td>
                  <td>{formatMoney(item.totalGross)}</td>
                  <td><Badge variant="success">{item.status}</Badge></td>
                </tr>
              ))}
              {!history.length && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--a-muted)', padding: '1.5rem' }}>No hay cierres Z.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      <Drawer isOpen={Boolean(selected)} onClose={() => setSelected(null)} title={selected ? `Cierre ${selected.type} ${selected.date}` : 'Cierre'}>
        {selected && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <Panel title="Totales">
              <div className="billing-row"><span>Bruto</span><strong>{formatMoney(selected.totalGross)}</strong></div>
              <div className="billing-row"><span>Neto</span><strong>{formatMoney(selected.totalNet)}</strong></div>
              <div className="billing-row"><span>IVA</span><strong>{formatMoney(selected.totalTax)}</strong></div>
            </Panel>
            <Button variant="secondary" iconLeft={<Download size={16} />} onClick={() => downloadPdf(selected).catch((err) => setToast({ variant: 'danger', message: err.message }))}>Descargar PDF Z</Button>
          </div>
        )}
      </Drawer>
    </Page>
  );
}
